import * as yup from "yup";
import psl from "psl";

/**
 * Length limits from RFC 5321 §4.5.3.1. A regex cannot count, so these are
 * enforced separately in `isValidEmail`.
 */
export const MAX_EMAIL_LENGTH = 254;
export const MAX_EMAIL_LOCAL_LENGTH = 64;

/**
 * EMAIL_REGEX — the pragmatic industry standard.
 *
 * CHANGE: replaces `/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.(com|org|net|in|co\.in)$/i`,
 * which hardcoded a five-entry TLD whitelist and so rejected every real address
 * on `.io`, `.dev`, `.app`, `.ai`, `.co.uk` and the ~1500 other live TLDs.
 *
 * This is the WHATWG HTML living-standard email pattern — the same shape
 * browsers apply to `<input type="email">` — tightened with the one rule a
 * business sign-up needs: the domain must be dotted and end in an alphabetic
 * TLD, so `user@localhost` and `user@intranet` are refused.
 *
 * WHAT IT ALLOWS, and why:
 *
 *   Local part (before the @)
 *     A-Z a-z 0-9 and  ! # $ % & ' * + / = ? ^ _ ` { | } ~ -
 *     Dots may SEPARATE those runs but may not lead, trail, or repeat, so
 *     `first.last` passes while `.a`, `a.`, and `a..b` do not.
 *     `+` is kept deliberately: plus-addressing (`kiran+hr@example.com`) is in
 *     everyday use and silently rejecting it looks like a broken form.
 *
 *   Domain (after the @)
 *     One or more dot-separated labels. Each label is 1-63 characters of
 *     A-Z a-z 0-9 and may contain hyphens INSIDE it but never at either end —
 *     which is what makes `procure-wiz.com` valid and `-wiz.com` / `wiz-.com`
 *     invalid. Underscores are not legal in a hostname and are rejected.
 *
 *   TLD
 *     2-63 letters. Digits are excluded because no TLD is numeric, and the
 *     2-character floor rejects typos like `user@example.c`.
 *
 * WHAT IT DELIBERATELY REFUSES, though RFC 5322 permits it:
 *   - quoted local parts, e.g. `"john doe"@example.com`
 *   - IP-literal domains, e.g. `user@[192.168.0.1]`
 *   - comments, e.g. `user(note)@example.com`
 *   No mainstream provider issues such addresses, and accepting them widens
 *   the input surface for no practical gain.
 *
 * Non-ASCII (IDN) domains such as `user@münchen.de` must be supplied in their
 * punycode form (`user@xn--mnchen-3ya.de`), which this accepts.
 *
 * NOTE: no `i` flag — the character classes already list both cases, and an
 * `i` flag on a class that spells out ranges only invites the belief that some
 * other part is case-sensitive.
 */
export const EMAIL_REGEX =
  /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,63}$/;

/**
 * True when the domain's suffix is one the IANA registry actually lists.
 *
 * CHANGE: EMAIL_REGEX alone could not reject `abc@gmail.commmmmmmmmmm`, and no
 * email-SYNTAX validator can — `commmmmmmmmmm` is a perfectly well-formed DNS
 * label, so that address is syntactically valid. (yup's own `.email()` accepts
 * it too, along with `user@localhost` and `a@b.c`.) The only way to refuse it
 * is to check the suffix against the real registry, which is what `psl` does
 * using the Mozilla Public Suffix List.
 *
 * Using the list rather than a hand-written set of TLDs means `.co.uk` and the
 * other multi-part suffixes are handled correctly, punycode IDN domains such as
 * `xn--mnchen-3ya.de` still pass, and newly delegated TLDs arrive with an
 * `npm update psl` instead of an edit here.
 *
 * NOTE: this proves the suffix is REGISTERED, not that the domain exists.
 * `gmail.cm` passes (`.cm` is Cameroon's real TLD) even though it is almost
 * certainly a typo for `.com`. Catching that needs an MX lookup on the server.
 */
const hasRegisteredTld = (domain: string): boolean => {
  try {
    return psl.isValid(domain.toLowerCase());
  } catch {
    // psl reports malformed input by return value, but a throw must never take
    // the form down with it — an unparsable domain is simply not valid.
    return false;
  }
};

/**
 * True when `email` is an address this application will accept.
 *
 * Trims first, because a trailing space from a paste is a formatting slip
 * rather than an invalid address.
 */
export const isValidEmail = (email: string): boolean => {
  const value = email.trim();

  if (value.length === 0 || value.length > MAX_EMAIL_LENGTH) return false;

  // The local part is everything before the LAST @, matching how the regex and
  // every mail server read it.
  const at = value.lastIndexOf("@");
  if (at === -1 || at > MAX_EMAIL_LOCAL_LENGTH) return false;

  // Shape first (cheap, and it guarantees there IS a dotted domain to inspect),
  // then the registry check on the domain.
  if (!EMAIL_REGEX.test(value)) return false;

  return hasRegisteredTld(value.slice(at + 1));
};

/**
 * Adds the shared email-format check to an existing string schema.
 *
 * NOTE: exists because EditEmployee cannot use `emailSchema` wholesale — its
 * email field is built from `validateNoTrailingOrLeadingWhitespace` and then
 * made conditionally required via `.when("userStatus", ...)`. Rather than
 * re-implement the format test there, that chain is passed through here, so
 * there is still exactly one definition of "is this email acceptable".
 */
export const withEmailFormat = <T extends yup.StringSchema>(
  schema: T,
  invalidMessage = "Please enter a valid email address",
): T =>
  // `!value ||` so an empty field reports "required" rather than "invalid";
  // when the field is optional, empty stays valid.
  schema.test("is-valid-email", invalidMessage, (value?: string) =>
    !value || isValidEmail(value),
  ) as T;

/**
 * The one email field schema for the whole application.
 *
 * CHANGE: email validation was split three ways — `isValidEmail` on the two
 * Registration screens and Login, and yup's much weaker built-in `.email()` on
 * seven other fields (ForgotPassword, ChangePassword, EditEmployee and the four
 * inventory `issuedTo` fields). The weak ones accepted `user@localhost`, so the
 * same address this helper refuses could still be saved from another screen.
 * Every email field now builds its schema here.
 *
 * Follows the `phoneNumberSchema` convention in util/phoneCountry: passing
 * `requiredMessage` makes the field required, omitting it leaves it optional.
 */
export const emailSchema = (options?: {
  requiredMessage?: string;
  invalidMessage?: string;
}) => {
  const base = yup.string();
  const schema = options?.requiredMessage
    ? base.required(options.requiredMessage)
    : base;

  return withEmailFormat(schema, options?.invalidMessage);
};

export const validateNoTrailingOrLeadingWhitespace = (fieldLabel: string) =>
  yup
    .string()
    .trim()
    .required(`${fieldLabel} is Required`)
    // .test(
    //   `not-only-spaces-${fieldLabel}`,
    //   "Empty whitespace not allowed in start and end",
    //   (value) => !!value && value.trim().length > 0
    // )
    // .test(
    //   `no-leading-trailing-space-${fieldLabel}`,
    //   "Empty whitespace not allowed in start and end",
    //   (value) => !!value && value === value.trim()
    // );


export const TrailingOrLeadingWhitespaceValidation = (fieldLabel: string) =>
  yup
    .string()
    // Do not use .trim() here, otherwise Yup mutates the string before the tests run
    .required(`${fieldLabel} is Required`)
    .test(
      `not-only-spaces-${fieldLabel}`,
      `${fieldLabel} cannot be only blank spaces`,
      (value) => !!value && value.trim().length > 0
    )
    .test(
      `no-leading-trailing-space-${fieldLabel}`,
      "Empty whitespace not allowed in start and end",
      (value) => !!value && value === value.trim()
    );

export const validateNoLeadingTrailingWhitespace = (value?: string) => {
  if (!value) return true; // Let .required() handle empty states
  return value.trim().length > 0; // Fails if the user enters ONLY spaces
};

/**
 * Formats IFSC input by removing non-alphanumeric characters 
 * and forcing the string to uppercase.
 */
export const handleIFSCInput = (e: React.FormEvent<HTMLInputElement>) => {
  const target = e.target as HTMLInputElement;

  // Replace anything that is NOT a letter or number, then uppercase it
  const formattedValue = target.value.replace(/[^A-Za-z0-9]/g, "").toUpperCase();

  // Enforce max length of 11 at the DOM level (optional but recommended)
  target.value = formattedValue.slice(0, 11);
};

/**
 * Validates an Indian Financial System Code (IFSC)
 * Standard: 4 Alphabets + 1 Zero + 6 Alphanumeric characters (Total exactly 11)
 */
export const validateIFSCCode = (ifsc: string | undefined | null): boolean => {
  if (!ifsc) return false;

  // Trim to ignore accidental leading/trailing spaces
  const cleanIFSC = ifsc.trim();

  // Regex pattern for IFSC
  const ifscRegex = /^[A-Za-z]{4}0[A-Za-z0-9]{6}$/;

  return ifscRegex.test(cleanIFSC);
};